
import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

interface TagManagerProps {
  initialTags: string[];
  onTagsChange: (tags: string[]) => void;
  disabled?: boolean;
}

export const TagManager: React.FC<TagManagerProps> = ({ 
  initialTags = [], 
  onTagsChange,
  disabled = false
}) => {
  const [tags, setTags] = useState<string[]>(initialTags);
  const [newTag, setNewTag] = useState<string>('');
  
  // Update tags when initialTags change (e.g., when loading a post)
  useEffect(() => {
    setTags(initialTags);
  }, [initialTags]);

  const handleAddTag = () => {
    if (!newTag.trim()) return;

    const tagToAdd = newTag.trim();
    
    // Check if tag already exists (case-insensitive)
    if (tags.some(tag => tag.toLowerCase() === tagToAdd.toLowerCase())) {
      toast.error('This tag already exists');
      return;
    }

    // Add the new tag
    const updatedTags = [...tags, tagToAdd];
    setTags(updatedTags);
    onTagsChange(updatedTags);
    setNewTag('');
  };

  const handleRemoveTag = (indexToRemove: number) => {
    const updatedTags = tags.filter((_, index) => index !== indexToRemove);
    setTags(updatedTags);
    onTagsChange(updatedTags);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, index) => (
          <Badge 
            key={`${tag}-${index}`} 
            variant="outline" 
            className="px-3 py-1.5 text-sm bg-white flex items-center gap-1 group"
          >
            {tag}
            {!disabled && (
              <button 
                type="button"
                onClick={() => handleRemoveTag(index)}
                className="ml-1 text-gray-400 hover:text-red-500 focus:outline-none"
                aria-label={`Remove tag ${tag}`}
              >
                <X size={14} />
              </button>
            )}
          </Badge>
        ))}
      </div>
      
      {!disabled && (
        <div className="flex">
          <Input
            type="text"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add a tag"
            className="mr-2"
            disabled={disabled}
          />
          <Button 
            type="button" 
            onClick={handleAddTag} 
            size="sm"
            variant="outline"
            disabled={!newTag.trim() || disabled}
          >
            <Plus size={16} className="mr-1" />
            Add
          </Button>
        </div>
      )}
    </div>
  );
};
